/**
 * Invoice View Component
 *
 * Displays a single invoice with all details and PDF preview option.
 */

import React, { useMemo } from 'react'

import { usePageHeader } from '@elsapiens/providers'
import { Link } from 'react-router-dom'

import {
  computeTaxBreakdown,
  getDocHeading,
  getDocLabel,
  getIsIntrastate,
  getSupplyTypeLabel,
  resolvePartyBillingAddress,
  resolveSellerAddress,
} from './invoice-view/invoice-view-helpers'
import { InvoiceViewAddresses } from './invoice-view/InvoiceViewAddresses'
import { InvoiceViewDetails } from './invoice-view/InvoiceViewDetails'
import { InvoiceViewHeader } from './invoice-view/InvoiceViewHeader'
import { InvoiceViewLineItems } from './invoice-view/InvoiceViewLineItems'
import { InvoiceViewTotals } from './invoice-view/InvoiceViewTotals'

import type { Invoice } from '../../types/billing'
import './InvoiceView.css'

// =============================================================================
// Types
// =============================================================================

export interface InvoiceViewProps {
  /** Invoice to display */
  invoice: Invoice
  /** Callback to go back to list */
  onBack: () => void
  /** Callback to edit the invoice */
  onEdit?: () => void
  /** Callback to clone the invoice into a new draft */
  onClone?: () => void
  /** Callback to generate PDF */
  onGeneratePDF: () => void
  /** Callback to post the invoice */
  onPost?: () => void
  /** Callback to void the invoice */
  onVoid?: () => void
  /** Callback to record a payment */
  onRecordPayment?: () => void
  /** Callback to view version history */
  onViewHistory?: () => void
  /** Whether actions are loading */
  isLoading?: boolean
  /** Payment panel rendered in the right column */
  paymentPanel?: React.ReactNode
}

// =============================================================================
// Component
// =============================================================================

interface InvoiceComputedFlags {
  isVendor: boolean
  isExport: boolean
  isIntrastate: boolean
  docLabel: string
  docHeading: string
}

function useInvoiceViewHeader(invoice: InvoiceViewProps['invoice'], flags: InvoiceComputedFlags): void {
  usePageHeader({
    title: `${flags.docLabel} ${invoice.invoiceNumber}`,
    breadcrumbs: [
      { label: 'Home', href: '/', as: Link },
      { label: flags.isVendor ? 'Posted Bills' : 'Posted Invoices', href: flags.isVendor ? '/bills/final' : '/invoices/final', as: Link },
      { label: `${flags.docLabel} ${invoice.invoiceNumber}` },
    ],
  })
}

function deriveInvoiceFlags(invoice: InvoiceViewProps['invoice']): InvoiceComputedFlags {
  return {
    isVendor: invoice.invoiceType === 'vendor_bill' || invoice.invoiceType === 'vendor_credit_note',
    isExport: invoice.invoiceType === 'export',
    isIntrastate: getIsIntrastate(invoice.supplyType),
    docLabel: getDocLabel(invoice.invoiceType),
    docHeading: getDocHeading(invoice.invoiceType),
  }
}

interface PreviewProps {
  invoice: InvoiceViewProps['invoice']
  flags: InvoiceComputedFlags
  sellerAddress: ReturnType<typeof resolveSellerAddress>
  partyBillingAddress: ReturnType<typeof resolvePartyBillingAddress>
  taxBreakdown: ReturnType<typeof computeTaxBreakdown>
  supplyTypeLabel: string
}

function InvoicePreview({ invoice, flags, sellerAddress, partyBillingAddress, taxBreakdown, supplyTypeLabel }: PreviewProps): JSX.Element {
  const { isVendor, isExport, isIntrastate, docHeading } = flags
  const legacyFields = invoice as unknown as import('./invoice-view/invoice-view-helpers').InvoiceLegacyFields
  return (
    <div className="invoice-preview">
      <InvoiceViewDetails
        baseCurrency={invoice.currency ?? 'INR'}
        docHeading={docHeading}
        invoice={invoice}
        isExport={isExport}
        isVendor={isVendor}
        partyBillingAddress={partyBillingAddress}
        sellerAddress={sellerAddress}
      />
      <InvoiceViewAddresses
        invoice={invoice}
        isExport={isExport}
        isVendor={isVendor}
        legacyFields={legacyFields}
        partyBillingAddress={partyBillingAddress}
        sellerAddress={sellerAddress}
      />
      {!isExport && invoice.placeOfSupply && (
        <div className="place-of-supply-section">
          <span className="place-of-supply-label">Place of Supply:</span>
          <span className="place-of-supply-value">{invoice.placeOfSupply}</span>
          <span className="supply-type-badge-inline">{supplyTypeLabel}</span>
        </div>
      )}
      <InvoiceViewLineItems invoice={invoice} isExport={isExport} />
      <InvoiceViewTotals
        invoice={invoice}
        isExport={isExport}
        isIntrastate={isIntrastate}
        isVendor={isVendor}
        taxBreakdown={taxBreakdown}
      />
      <div className="preview-footer">
        {invoice.status === 'voided' && <div className="void-stamp">VOIDED</div>}
        <p>Generated by elBooks</p>
      </div>
    </div>
  )
}

interface InvoiceViewBodyProps {
  invoice: InvoiceViewProps['invoice']
  flags: InvoiceComputedFlags
  sellerAddress: ReturnType<typeof resolveSellerAddress>
  partyBillingAddress: ReturnType<typeof resolvePartyBillingAddress>
  taxBreakdown: ReturnType<typeof computeTaxBreakdown>
  supplyTypeLabel: string
  paymentPanel?: React.ReactNode
}

function InvoiceViewBody({
  invoice, flags, sellerAddress, partyBillingAddress, taxBreakdown, supplyTypeLabel, paymentPanel,
}: InvoiceViewBodyProps): JSX.Element {
  return (
    <div className="el-p-lg">
      <div className="invoice-split-layout">
        <div className="invoice-view-container">
          <InvoicePreview
            flags={flags}
            invoice={invoice}
            partyBillingAddress={partyBillingAddress}
            sellerAddress={sellerAddress}
            supplyTypeLabel={supplyTypeLabel}
            taxBreakdown={taxBreakdown}
          />
        </div>
        {paymentPanel && <div className="invoice-payment-panel">{paymentPanel}</div>}
      </div>
    </div>
  )
}

export function InvoiceView({
  invoice,
  onBack: _onBack,
  onEdit,
  onClone,
  onGeneratePDF,
  onPost,
  onVoid,
  onRecordPayment,
  onViewHistory,
  isLoading = false,
  paymentPanel,
}: InvoiceViewProps): JSX.Element {
  const flags = deriveInvoiceFlags(invoice)
  const supplyTypeLabel = useMemo(() => getSupplyTypeLabel(invoice.supplyType) ?? '', [invoice.supplyType])
  const taxBreakdown = useMemo(() => computeTaxBreakdown(invoice.items, flags.isIntrastate), [invoice.items, flags.isIntrastate])
  const sellerAddress = useMemo(() => resolveSellerAddress(invoice), [invoice])
  const partyBillingAddress = useMemo(() => resolvePartyBillingAddress(invoice), [invoice])

  useInvoiceViewHeader(invoice, flags)

  return (
    <div className="flex flex-col">
      <InvoiceViewHeader
        docLabel={flags.docLabel}
        invoice={invoice}
        isLoading={isLoading}
        onClone={onClone}
        onEdit={onEdit}
        onGeneratePDF={onGeneratePDF}
        onPost={onPost}
        onRecordPayment={onRecordPayment}
        onViewHistory={onViewHistory}
        onVoid={onVoid}
      />
      <InvoiceViewBody
        flags={flags}
        invoice={invoice}
        partyBillingAddress={partyBillingAddress}
        paymentPanel={paymentPanel}
        sellerAddress={sellerAddress}
        supplyTypeLabel={supplyTypeLabel}
        taxBreakdown={taxBreakdown}
      />
    </div>
  )
}

export default InvoiceView
